import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheckCircle,
  faClock,
  faEye,
  faFileExport,
  faSpinner,
  faSyncAlt,
  faTimes,
  faTimesCircle,
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-hot-toast";
import { BASE_URL } from "../../config";

const STATUS_OPTIONS = ["PENDING", "APPROVED", "REJECTED"];

const statusClassName = (status) => {
  const normalized = String(status || "").toUpperCase();
  if (normalized === "APPROVED") return "bg-emerald-100 text-emerald-700";
  if (normalized === "REJECTED") return "bg-rose-100 text-rose-700";
  return "bg-amber-100 text-amber-700";
};

const formatRequestType = (value) => {
  const normalized = String(value || "").toUpperCase();
  if (normalized === "MARK_NOT_VALID_RTP") return "Mark Not Valid For RTP";
  if (normalized === "RECONSIDER_OVERRIDE") return "Reconsider Override";
  if (normalized === "MARK_VALID_RTP") return "Mark as Valid RTP";
  return value || "-";
};

const resolveErrorMessage = (error, fallback) => {
  const message = error?.response?.data?.message;
  if (!message) return fallback;
  if (typeof message === "string") return message;
  if (Array.isArray(message)) return message.join(", ");
  if (typeof message === "object") return Object.values(message).flat().join(", ");
  return fallback;
};

const getDecisionOutcomeText = (reason, action) => {
  const normalizedReason = String(reason || "").toUpperCase();
  if (action === "approve") {
    return normalizedReason === "MARK_VALID_RTP"
      ? "Result after approval: claim stays For RTP (rtp_tag = 1)."
      : "Result after approval: claim moves to For Processing (rtp_tag = 0).";
  }
  return normalizedReason === "MARK_VALID_RTP"
    ? "Result after rejection: claim moves to For Processing (rtp_tag = 0)."
    : "Result after rejection: claim stays For RTP (rtp_tag = 1).";
};

const getBulkRuleText = (action) =>
  action === "approve"
    ? "Approve rule: Mark as Valid RTP stays For RTP. Other request types become For Processing."
    : "Reject rule: Mark as Valid RTP becomes For Processing. Other request types stay For RTP.";

const ClaimsRtpApprovals = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isDeciding, setIsDeciding] = useState(false);

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  const [status, setStatus] = useState("PENDING");
  const [search, setSearch] = useState("");
  const [batchNo, setBatchNo] = useState("");

  const [selectedRow, setSelectedRow] = useState(null);
  const [decisionAction, setDecisionAction] = useState("");
  const [decisionRemarks, setDecisionRemarks] = useState("");
  const [selectedApprovalIds, setSelectedApprovalIds] = useState([]);
  const [selectionProviderCode, setSelectionProviderCode] = useState("");
  const [selectionBatchNo, setSelectionBatchNo] = useState("");
  const [bulkDecisionAction, setBulkDecisionAction] = useState("");
  const [bulkDecisionRemarks, setBulkDecisionRemarks] = useState("");
  const [bulkRowsForDecision, setBulkRowsForDecision] = useState([]);
  const [isExportingCsv, setIsExportingCsv] = useState(false);

  const token = localStorage.getItem("authentication/token");

  const fetchApprovals = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${BASE_URL}/claims/processing/rtp-approval-requests`,
        {
          params: {
            status,
            batch_no: batchNo.trim(),
            search: search.trim(),
            page,
            per_page: perPage,
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      setRows(response.data?.data || []);
      const pagination = response.data?.pagination || {};
      setTotalPages(pagination.total_pages || 1);
      setTotalRecords(pagination.total || 0);
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to fetch Final For RTP approval queue.",
        { duration: 4000 },
      );
      setRows([]);
      setTotalPages(1);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  }, [status, batchNo, search, page, perPage, token]);

  useEffect(() => {
    fetchApprovals();
  }, [fetchApprovals]);

  useEffect(() => {
    const validRows = new Map(
      rows
        .filter((row) => String(row.status || "").toUpperCase() === "PENDING")
        .map((row) => [Number(row.id), row]),
    );

    setSelectedApprovalIds((prev) => {
      const next = prev.filter((id) => validRows.has(Number(id)));
      if (next.length === 0) {
        setSelectionProviderCode("");
        setSelectionBatchNo("");
      }
      return next;
    });
  }, [rows]);

  const showingText = useMemo(() => {
    if (!totalRecords) return "No records";
    const from = (page - 1) * perPage + 1;
    const to = Math.min(page * perPage, totalRecords);
    return `Showing ${from} to ${to} of ${totalRecords}`;
  }, [page, perPage, totalRecords]);

  const getSelectionContext = (row) => ({
    providerCode: String(row.provider_code || "").toUpperCase(),
    batchNo: String(row.batch_no || "").toUpperCase(),
  });

  const isSameSelectionContext = (row) => {
    if (!selectionProviderCode && !selectionBatchNo) return true;
    const context = getSelectionContext(row);
    return (
      context.providerCode === selectionProviderCode &&
      context.batchNo === selectionBatchNo
    );
  };

  const clearSelection = () => {
    setSelectedApprovalIds([]);
    setSelectionProviderCode("");
    setSelectionBatchNo("");
  };

  const toggleRowSelection = (row) => {
    const normalizedStatus = String(row.status || "").toUpperCase();
    if (normalizedStatus !== "PENDING") return;

    const rowId = Number(row.id);
    const isSelected = selectedApprovalIds.includes(rowId);
    if (isSelected) {
      const nextSelected = selectedApprovalIds.filter((id) => id !== rowId);
      setSelectedApprovalIds(nextSelected);
      if (nextSelected.length === 0) {
        setSelectionProviderCode("");
        setSelectionBatchNo("");
      }
      return;
    }

    const context = getSelectionContext(row);
    if (selectedApprovalIds.length > 0 && !isSameSelectionContext(row)) {
      toast.error("Bulk selection only allows the same provider and same batch number.", {
        duration: 4000,
      });
      return;
    }

    setSelectedApprovalIds((prev) => [...prev, rowId]);
    if (selectedApprovalIds.length === 0) {
      setSelectionProviderCode(context.providerCode);
      setSelectionBatchNo(context.batchNo);
    }
  };

  const pendingRows = useMemo(
    () => rows.filter((row) => String(row.status || "").toUpperCase() === "PENDING"),
    [rows],
  );

  const allPendingSelected =
    pendingRows.length > 0 &&
    pendingRows.every((row) => selectedApprovalIds.includes(Number(row.id)));

  const toggleSelectAllCurrentPage = () => {
    if (pendingRows.length === 0) return;

    if (allPendingSelected) {
      clearSelection();
      return;
    }

    const contexts = Array.from(
      new Set(pendingRows.map((row) => `${String(row.provider_code || "").toUpperCase()}|${String(row.batch_no || "").toUpperCase()}`)),
    );

    if (contexts.length > 1 && (!selectionProviderCode || !selectionBatchNo)) {
      toast.error("This page has mixed provider/batch. Select rows manually for one provider and batch only.", {
        duration: 4500,
      });
      return;
    }

    const targetProviderCode =
      selectionProviderCode || String(pendingRows[0].provider_code || "").toUpperCase();
    const targetBatchNo =
      selectionBatchNo || String(pendingRows[0].batch_no || "").toUpperCase();

    const matchingRows = pendingRows.filter((row) => {
      const context = getSelectionContext(row);
      return context.providerCode === targetProviderCode && context.batchNo === targetBatchNo;
    });

    setSelectionProviderCode(targetProviderCode);
    setSelectionBatchNo(targetBatchNo);
    setSelectedApprovalIds(matchingRows.map((row) => Number(row.id)));
  };

  const openDecisionModal = (row, action) => {
    setSelectedRow(row);
    setDecisionAction(action);
    setDecisionRemarks("");
  };

  const closeDecisionModal = () => {
    setSelectedRow(null);
    setDecisionAction("");
    setDecisionRemarks("");
    setIsDeciding(false);
  };

  const closeBulkDecisionModal = () => {
    setBulkDecisionAction("");
    setBulkDecisionRemarks("");
    setBulkRowsForDecision([]);
    setIsDeciding(false);
  };

  const openBulkDecisionModal = (action) => {
    if (selectedApprovalIds.length === 0) {
      toast.error("Please select at least one pending request.", { duration: 4000 });
      return;
    }

    const selectedRowsSet = new Set(selectedApprovalIds.map((id) => Number(id)));
    const rowsForDecision = rows.filter((row) => selectedRowsSet.has(Number(row.id)));
    if (rowsForDecision.length === 0) {
      toast.error("No valid selected rows on this page.", { duration: 4000 });
      return;
    }

    setBulkDecisionAction(action);
    setBulkDecisionRemarks("");
    setBulkRowsForDecision(rowsForDecision);
  };

  const submitDecision = async () => {
    if (!selectedRow?.id || !decisionAction) return;

    if (decisionAction === "reject" && !decisionRemarks.trim()) {
      toast.error("Remarks are required when rejecting a request.", {
        duration: 4000,
      });
      return;
    }

    try {
      setIsDeciding(true);
      await axios.post(
        `${BASE_URL}/claims/processing/rtp-approval-requests/${selectedRow.id}/decide`,
        {
          action: decisionAction,
          remarks: decisionRemarks.trim(),
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      toast.success("Approval decision saved.", { duration: 3000 });
      closeDecisionModal();
      fetchApprovals();
    } catch (error) {
      toast.error(resolveErrorMessage(error, "Failed to save approval decision."), { duration: 4000 });
      setIsDeciding(false);
    }
  };

  const submitBulkDecision = async () => {
    if (!bulkDecisionAction || bulkRowsForDecision.length === 0) return;

    if (bulkDecisionAction === "reject" && !bulkDecisionRemarks.trim()) {
      toast.error("Remarks are required when rejecting requests.", { duration: 4000 });
      return;
    }

    try {
      setIsDeciding(true);
      await axios.post(
        `${BASE_URL}/claims/processing/rtp-approval-requests/bulk-decide`,
        {
          ids: bulkRowsForDecision.map((row) => Number(row.id)),
          action: bulkDecisionAction,
          remarks: bulkDecisionRemarks.trim(),
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      toast.success("Bulk approval decision saved.", { duration: 3000 });
      clearSelection();
      closeBulkDecisionModal();
      fetchApprovals();
    } catch (error) {
      toast.error(resolveErrorMessage(error, "Failed to save bulk approval decision."), { duration: 4000 });
    } finally {
      setIsDeciding(false);
    }
  };

  const exportRtpApprovalsCsv = async () => {
    try {
      setIsExportingCsv(true);
      const response = await axios.get(
        `${BASE_URL}/claims/processing/rtp-approval-requests/export`,
        {
          params: {
            status,
            batch_no: batchNo.trim(),
            search: search.trim(),
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
          responseType: "blob",
        },
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `claims-rtp-approval-queue-${Date.now()}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Final RTP Approval Queue CSV export generated.", { duration: 3000 });
    } catch (error) {
      toast.error(resolveErrorMessage(error, "Failed to export Final RTP Approval Queue CSV."), {
        duration: 4000,
      });
    } finally {
      setIsExportingCsv(false);
    }
  };

  const applyFilters = () => {
    setPage(1);
    fetchApprovals();
  };

  const resetFilters = () => {
    setStatus("PENDING");
    setSearch("");
    setBatchNo("");
    setPage(1);
    clearSelection();
  };

  const bulkReasonCounts = useMemo(
    () =>
      bulkRowsForDecision.reduce((acc, row) => {
        const key = String(row.reason || "").toUpperCase();
        acc[key] = (acc[key] || 0) + 1;
        return acc;
      }, {}),
    [bulkRowsForDecision],
  );

  return (
    <div className="p-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Final For RTP Approval Queue</h1>
          <p className="text-sm text-gray-600">
            Review Mark Valid / Mark Not Valid / Reconsider requests from For RTP Entry.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={exportRtpApprovalsCsv}
            disabled={isExportingCsv}
            className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <FontAwesomeIcon icon={isExportingCsv ? faSpinner : faFileExport} spin={isExportingCsv} className="mr-2" />
            {isExportingCsv ? "Exporting..." : "Export CSV"}
          </button>
          <button
            onClick={fetchApprovals}
            className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm hover:bg-gray-50"
          >
            <FontAwesomeIcon icon={faSyncAlt} className="mr-2" />
            Refresh
          </button>
        </div>
      </div>

      <div className="mb-4 rounded-xl border border-gray-200 bg-white p-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
          <div>
            <label className="text-xs text-gray-600">Status</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-gray-600">Batch #</label>
            <input
              value={batchNo}
              onChange={(e) => setBatchNo(e.target.value)}
              placeholder="Batch no"
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="text-xs text-gray-600">Search</label>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="LOA / Trans / Reason / Requestor"
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="text-xs text-gray-600">Per Page</label>
            <select
              value={perPage}
              onChange={(e) => setPerPage(Number(e.target.value))}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
            >
              {[10, 20, 50, 100, 200, 500, 1000, 1500].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="mt-3 flex gap-2">
          <button
            onClick={applyFilters}
            className="rounded-lg bg-gray-900 px-3 py-2 text-sm text-white"
          >
            Apply Filters
          </button>
          <button
            onClick={resetFilters}
            className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm"
          >
            Reset
          </button>
        </div>
      </div>

      <div className="mb-4 rounded-xl border border-gray-200 bg-white p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="text-sm text-gray-600">
            Selected: <span className="font-semibold text-gray-900">{selectedApprovalIds.length}</span>
            {selectedApprovalIds.length > 0 && (
              <>
                {" "} | Provider: <span className="font-semibold text-gray-900">{selectionProviderCode || "-"}</span>
                {" "} | Batch: <span className="font-semibold text-gray-900">{selectionBatchNo || "-"}</span>
              </>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => openBulkDecisionModal("approve")}
              disabled={selectedApprovalIds.length === 0 || isDeciding}
              className="rounded-lg bg-emerald-600 px-3 py-2 text-xs font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50 hover:bg-emerald-700"
            >
              Bulk Approve
            </button>
            <button
              onClick={() => openBulkDecisionModal("reject")}
              disabled={selectedApprovalIds.length === 0 || isDeciding}
              className="rounded-lg bg-rose-600 px-3 py-2 text-xs font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50 hover:bg-rose-700"
            >
              Bulk Reject
            </button>
            <button
              onClick={clearSelection}
              disabled={selectedApprovalIds.length === 0}
              className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-700 disabled:cursor-not-allowed disabled:opacity-50 hover:bg-gray-50"
            >
              Clear Selection
            </button>
          </div>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
        <div className="flex items-center justify-between p-3 text-sm text-gray-600">
          <span>{showingText}</span>
          <span className="inline-flex items-center gap-1">
            <FontAwesomeIcon icon={faClock} />
            Final approval workflow queue
          </span>
        </div>
        <div className="overflow-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-700">
              <tr>
                <th className="p-3 text-left">
                  <input
                    type="checkbox"
                    checked={allPendingSelected}
                    onChange={toggleSelectAllCurrentPage}
                    title="Select all pending on this page (same provider and batch only)"
                  />
                </th>
                <th className="p-3 text-left">Requested At</th>
                <th className="p-3 text-left">Batch #</th>
                <th className="p-3 text-left">Transaction #</th>
                <th className="p-3 text-left">LOA #</th>
                <th className="p-3 text-left">Provider</th>
                <th className="p-3 text-left">Member</th>
                <th className="p-3 text-left">Claim Status</th>
                <th className="p-3 text-left">Reason</th>
                <th className="p-3 text-left">Remarks</th>
                <th className="p-3 text-left">Attachments</th>
                <th className="p-3 text-left">Status</th>
                <th className="p-3 text-left">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {loading ? (
                <tr>
                  <td colSpan="13" className="p-5 text-center text-gray-500">
                    <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                    Loading approval requests...
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan="13" className="p-5 text-center text-gray-500">
                    No approval requests found.
                  </td>
                </tr>
              ) : (
                rows.map((row) => {
                  const normalizedStatus = String(row.status || "").toUpperCase();
                  const rowId = Number(row.id);
                  const isSelected = selectedApprovalIds.includes(rowId);
                  const rowSelectable =
                    normalizedStatus === "PENDING" &&
                    (isSelected || isSameSelectionContext(row) || selectedApprovalIds.length === 0);
                  return (
                    <tr key={row.id}>
                      <td className="p-3">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          disabled={!rowSelectable}
                          onChange={() => toggleRowSelection(row)}
                          title={
                            rowSelectable
                              ? "Select row"
                              : "Only same provider and same batch can be selected"
                          }
                        />
                      </td>
                      <td className="p-3">{row.created_at || "-"}</td>
                      <td className="p-3">{row.batch_no || "-"}</td>
                      <td className="p-3">{row.trans_no || "-"}</td>
                      <td className="p-3">{row.loa_no || "-"}</td>
                      <td className="p-3">{row.provider_name || row.provider_code || "-"}</td>
                      <td className="p-3">{row.member_name || "-"}</td>
                      <td className="p-3">{row.claim_status || "-"}</td>
                      <td className="p-3">{formatRequestType(row.reason)}</td>
                      <td className="max-w-[280px] break-words p-3 text-xs text-gray-600">
                        {row.remarks || "-"}
                      </td>
                      <td className="p-3">
                        {Array.isArray(row.attachments) && row.attachments.length > 0 ? (
                          <ul className="space-y-1">
                            {row.attachments.map((file) => (
                              <li key={file.upload_id}>
                                <a
                                  href={file.signed_url}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-xs text-blue-600 hover:underline"
                                >
                                  {file.original_file_name}
                                </a>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <span className="text-xs text-gray-500">No attachment</span>
                        )}
                      </td>
                      <td className="p-3">
                        <span
                          className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${statusClassName(
                            normalizedStatus,
                          )}`}
                        >
                          {normalizedStatus || "-"}
                        </span>
                      </td>
                      <td className="p-3">
                        <div className="flex flex-wrap gap-2">
                          <button
                            onClick={() => setSelectedRow(row)}
                            className="rounded-lg bg-slate-600 px-2 py-1 text-xs text-white hover:bg-slate-700"
                          >
                            <FontAwesomeIcon icon={faEye} className="mr-1" />
                            View
                          </button>
                          {normalizedStatus === "PENDING" && (
                            <>
                              <button
                                onClick={() => openDecisionModal(row, "approve")}
                                className="rounded-lg bg-emerald-600 px-2 py-1 text-xs text-white hover:bg-emerald-700"
                              >
                                <FontAwesomeIcon icon={faCheckCircle} className="mr-1" />
                                Approve
                              </button>
                              <button
                                onClick={() => openDecisionModal(row, "reject")}
                                className="rounded-lg bg-rose-600 px-2 py-1 text-xs text-white hover:bg-rose-700"
                              >
                                <FontAwesomeIcon icon={faTimesCircle} className="mr-1" />
                                Reject
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between p-3 text-sm text-gray-600">
          <button
            onClick={() => setPage((prev) => Math.max(1, prev - 1))}
            disabled={page === 1}
            className="rounded-md border border-gray-200 px-3 py-1 disabled:opacity-50"
          >
            Previous
          </button>
          <span>
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
            disabled={page >= totalPages}
            className="rounded-md border border-gray-200 px-3 py-1 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>

      {selectedRow && !decisionAction && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-3xl rounded-xl bg-white p-6 shadow-lg">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Approval Request Details</h2>
                <p className="text-xs text-gray-500">Request #{selectedRow.id}</p>
              </div>
              <button
                onClick={closeDecisionModal}
                className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm"
              >
                Close
              </button>
            </div>
            <div className="grid grid-cols-1 gap-3 text-sm md:grid-cols-2">
              <div className="rounded-lg border border-gray-100 p-3">
                <div className="text-xs text-gray-500">Batch #</div>
                <div>{selectedRow.batch_no || "-"}</div>
              </div>
              <div className="rounded-lg border border-gray-100 p-3">
                <div className="text-xs text-gray-500">Transaction #</div>
                <div>{selectedRow.trans_no || "-"}</div>
              </div>
              <div className="rounded-lg border border-gray-100 p-3">
                <div className="text-xs text-gray-500">LOA #</div>
                <div>{selectedRow.loa_no || "-"}</div>
              </div>
              <div className="rounded-lg border border-gray-100 p-3">
                <div className="text-xs text-gray-500">Claim Status</div>
                <div>{selectedRow.claim_status || "-"}</div>
              </div>
              <div className="rounded-lg border border-gray-100 p-3">
                <div className="text-xs text-gray-500">Status</div>
                <div>{selectedRow.status || "-"}</div>
              </div>
              <div className="rounded-lg border border-gray-100 p-3">
                <div className="text-xs text-gray-500">Requested By</div>
                <div>{selectedRow.requested_by_name || "-"}</div>
              </div>
              <div className="rounded-lg border border-gray-100 p-3">
                <div className="text-xs text-gray-500">Reviewed By</div>
                <div>{selectedRow.reviewed_by_name || "-"}</div>
              </div>
              <div className="rounded-lg border border-gray-100 p-3 md:col-span-2">
                <div className="text-xs text-gray-500">Reason</div>
                <div>{formatRequestType(selectedRow.reason)}</div>
              </div>
              <div className="rounded-lg border border-gray-100 p-3 md:col-span-2">
                <div className="text-xs text-gray-500">Remarks</div>
                <div>{selectedRow.remarks || "-"}</div>
              </div>
              <div className="rounded-lg border border-gray-100 p-3 md:col-span-2">
                <div className="text-xs text-gray-500">Approver Remarks</div>
                <div>{selectedRow.reviewed_remarks || "-"}</div>
              </div>
              <div className="rounded-lg border border-gray-100 p-3 md:col-span-2">
                <div className="text-xs text-gray-500">Attachments</div>
                {Array.isArray(selectedRow.attachments) && selectedRow.attachments.length > 0 ? (
                  <ul className="mt-2 space-y-1">
                    {selectedRow.attachments.map((file) => (
                      <li key={file.upload_id}>
                        <a
                          href={file.signed_url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-blue-600 hover:underline"
                        >
                          {file.original_file_name}
                        </a>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div>No attachment</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedRow && decisionAction && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-xl">
            <div
              className={`bg-gradient-to-r px-5 py-4 text-white ${
                decisionAction === "approve"
                  ? "from-emerald-600 to-emerald-500"
                  : "from-rose-600 to-rose-500"
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-[0.14em] text-white/80">
                    Final Approval Decision
                  </div>
                  <h3 className="mt-1 text-lg font-semibold">
                    {decisionAction === "approve" ? "Approve Request" : "Reject Request"}
                  </h3>
                  <p className="mt-1 text-xs text-white/85">
                    Transaction #{selectedRow.trans_no || "-"} | LOA #{selectedRow.loa_no || "-"}
                  </p>
                </div>
                <button
                  onClick={closeDecisionModal}
                  className="rounded-lg border border-white/40 bg-white/10 px-2 py-1 text-xs hover:bg-white/20"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
            </div>

            <div className="space-y-4 p-5">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                  <div className="text-xs uppercase tracking-wide text-slate-500">Request Type</div>
                  <div className="mt-1 text-sm font-semibold text-slate-900">
                    {formatRequestType(selectedRow.reason)}
                  </div>
                </div>
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                  <div className="text-xs uppercase tracking-wide text-slate-500">Approval Rule</div>
                  <div className="mt-1 text-xs font-semibold text-slate-800">
                    {getBulkRuleText(decisionAction)}
                  </div>
                </div>
              </div>

              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                <div className="text-xs uppercase tracking-wide text-slate-500">Decision Impact</div>
                <div className="mt-1 text-sm font-semibold text-slate-900">
                  {getDecisionOutcomeText(selectedRow.reason, decisionAction)}
                </div>
              </div>

              <div>
                <label className="text-xs text-gray-500">Approver Remarks</label>
                <textarea
                  value={decisionRemarks}
                  onChange={(e) => setDecisionRemarks(e.target.value)}
                  rows={4}
                  placeholder={
                    decisionAction === "approve"
                      ? "Optional remarks for approval"
                      : "Reason for rejection (required)"
                  }
                  className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 border-t border-slate-200 bg-slate-50 px-5 py-4">
              <button
                onClick={closeDecisionModal}
                className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm"
              >
                Cancel
              </button>
              <button
                onClick={submitDecision}
                disabled={isDeciding}
                className={`rounded-lg px-4 py-2 text-sm text-white disabled:opacity-50 ${
                  decisionAction === "approve"
                    ? "bg-emerald-600 hover:bg-emerald-700"
                    : "bg-rose-600 hover:bg-rose-700"
                }`}
              >
                {isDeciding ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                    Saving...
                  </>
                ) : decisionAction === "approve" ? (
                  "Confirm Approve"
                ) : (
                  "Confirm Reject"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {bulkDecisionAction && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4">
          <div className="w-full max-w-5xl overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div
              className={`bg-gradient-to-r px-5 py-4 text-white ${
                bulkDecisionAction === "approve"
                  ? "from-emerald-600 to-emerald-500"
                  : "from-rose-600 to-rose-500"
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-[0.14em] text-white/80">
                    Final RTP Bulk Decision
                  </div>
                  <h3 className="mt-1 text-xl font-semibold">
                    {bulkDecisionAction === "approve"
                      ? "Bulk Approve Selected Requests"
                      : "Bulk Reject Selected Requests"}
                  </h3>
                  <p className="mt-1 text-sm text-white/85">
                    Provider: {bulkRowsForDecision[0]?.provider_name || bulkRowsForDecision[0]?.provider_code || "-"}
                    {" "} | Batch: {bulkRowsForDecision[0]?.batch_no || "-"}
                  </p>
                </div>
                <button
                  onClick={closeBulkDecisionModal}
                  className="rounded-lg border border-white/40 bg-white/10 px-2 py-1 text-xs hover:bg-white/20"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
            </div>

            <div className="space-y-4 p-5">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                  <div className="text-xs uppercase tracking-wide text-slate-500">Selected Rows</div>
                  <div className="mt-1 text-lg font-semibold text-slate-900">{bulkRowsForDecision.length}</div>
                </div>
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 md:col-span-2">
                  <div className="text-xs uppercase tracking-wide text-slate-500">Approval Rule</div>
                  <div className="mt-1 text-sm font-semibold text-slate-900">
                    {getBulkRuleText(bulkDecisionAction)}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
                {Object.keys(bulkReasonCounts).map((key) => (
                  <div key={key} className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm">
                    <div className="font-semibold text-slate-800">{formatRequestType(key)}</div>
                    <div className="text-xs text-slate-500">{bulkReasonCounts[key]} request(s)</div>
                  </div>
                ))}
              </div>

              <div className="overflow-hidden rounded-xl border border-slate-200">
                <div className="bg-slate-50 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-600">
                  Selected Approval Requests
                </div>
                <div className="max-h-64 overflow-auto">
                  <table className="min-w-full text-xs">
                    <thead className="bg-white text-slate-600">
                      <tr>
                        <th className="p-2 text-left">Request #</th>
                        <th className="p-2 text-left">Transaction #</th>
                        <th className="p-2 text-left">LOA #</th>
                        <th className="p-2 text-left">Claim Status</th>
                        <th className="p-2 text-left">Request Type</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 bg-white">
                      {bulkRowsForDecision.map((row) => (
                        <tr key={`bulk-row-${row.id}`}>
                          <td className="p-2">{row.id}</td>
                          <td className="p-2">{row.trans_no || "-"}</td>
                          <td className="p-2">{row.loa_no || "-"}</td>
                          <td className="p-2">{row.claim_status || "-"}</td>
                          <td className="p-2">{formatRequestType(row.reason)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div>
                <label className="text-xs text-gray-500">Approver Remarks</label>
                <textarea
                  value={bulkDecisionRemarks}
                  onChange={(e) => setBulkDecisionRemarks(e.target.value)}
                  rows={3}
                  placeholder={
                    bulkDecisionAction === "reject"
                      ? "Reason for bulk rejection (required)"
                      : "Optional remarks for bulk approval"
                  }
                  className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 border-t border-slate-200 bg-slate-50 px-5 py-4">
              <button
                onClick={closeBulkDecisionModal}
                className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm"
              >
                Cancel
              </button>
              <button
                onClick={submitBulkDecision}
                disabled={isDeciding}
                className={`rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-50 ${
                  bulkDecisionAction === "approve"
                    ? "bg-emerald-600 hover:bg-emerald-700"
                    : "bg-rose-600 hover:bg-rose-700"
                }`}
              >
                {isDeciding ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                    Saving...
                  </>
                ) : bulkDecisionAction === "approve" ? (
                  "Confirm Bulk Approve"
                ) : (
                  "Confirm Bulk Reject"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClaimsRtpApprovals;
